/**
 * 烘干厂端 菜单总配置 (T-017)
 *
 * 按思维导图设计的 8 个 L1 分组 + L2 菜单项. 单一来源, 同时给:
 *   1) MainLayout 渲染 (8 个折叠组)
 *   2) router 注册路由 (其中 placeholder=true 的指向共享 Placeholder.vue)
 *   3) menuPermissions 权限映射 (key + perm)
 *
 * 每个 item 字段:
 *   key         路由 name (kebab-case)
 *   label       菜单显示文字
 *   icon        antdv icon 名 (https://1x.antdv.com/components/icon-cn/)
 *   perm        sys_permission.id, 后端按 token 查用户菜单权限时返回的 ID
 *   placeholder true=空壳"建设中"页面, 共用 Placeholder.vue
 *   existing    true=该 key 对应已有的 .vue 文件 (entry/exit/seed 等), router 已有路由
 *   superOnly   true=仅粮易智联超级管理员 (SUPPER_ADMIN) 可见可访问 (同「外设服务版本」的管控方式,
 *               区别于 DRY_SUPER_ADMIN 烘干厂管理员); 菜单隐藏 + 路由 requireSuperAdmin 守卫
 */

export const MENU_GROUPS = [
  {
    key: 'grain', label: '粮食模块', icon: 'database',
    items: [
      // [2026-09-08] 粮食入库改名「粮食过磅录入」(与新增的免过磅手工录入页区分; DB 同步 UPDATE, 见 V20260908_T3)
      { key: 'entry-v1', label: '粮食过磅录入', icon: 'thunderbolt', perm: 'DRY_ENTRY_V1', existing: true },
      // [2026-09-08] 粮食手工录入 — 与过磅录入共用 EntryV1 页面 (第四个 tab「手工录入」),
      //   菜单直达 /manual-entry, 页内路由监听自动切 tab; 入库单管理/入库退货 tab 原样复用
      { key: 'manual-entry', label: '粮食手工录入', icon: 'edit', perm: 'DRY_GRAIN_MANUAL_ENTRY', existing: true },
      { key: 'exit',     label: '粮食出库',    icon: 'export',     perm: 'DRY_EXIT',     existing: true }
    ]
  },
  {
    key: 'agri', label: '农资模块', icon: 'experiment',
    items: [
      { key: 'seed-in', label: '农资入库', icon: 'import', perm: 'DRY_SEED_IN', existing: true },
      { key: 'seed-out', label: '农资出库', icon: 'export', perm: 'DRY_SEED_OUT', existing: true }
      // [6.12会议] 删除种子销售菜单 (key=seed) — 业务确认不需要
    ]
  },
  {
    key: 'customer', label: '客商管理', icon: 'team',
    items: [
      // T-017 fix: 客商管理直接用现有的 Farmer.vue (思维导图旁注: 农户从粮联同步过来, 仅补充信息)
      { key: 'customer', label: '客商管理',   icon: 'team',  perm: 'DRY_CUSTOMER', existing: true },
      { key: 'driver',   label: '司机与车辆', icon: 'car',   perm: 'DRY_DRIVER',   existing: true },
      // [2026-07-07] 农户管理隐藏 — 农户/客商/供应商统一在「客商管理」维护, 此 legacy 菜单废弃 (路由与 Farmer.vue 保留, URL 直达仍可用)
      // { key: 'farmer',   label: '农户管理',   icon: 'user',  perm: 'DRY_FARMER',   existing: true, legacy: true }
    ]
  },
  {
    // 商品管理 — 大菜单, 子菜单为商品档案 (与其他分组结构统一, 均为折叠子菜单)
    key: 'product', label: '商品管理', icon: 'gold',
    items: [
      { key: 'product', label: '商品档案', icon: 'gold', perm: 'DRY_PRODUCT_GRAIN' },
      // [2026-06-25] 质价公式从系统管理移入 (属商品维度配置)
      { key: 'quality-price', label: '质价公式', icon: 'calculator', perm: 'DRY_SYS_QUALITY_PRICE', existing: true },
      // [2026-08-14] 干粮换算从系统管理移入 (属商品维度配置; perm 不变: DRY_SYS_FORMULA)
      { key: 'formula', label: '干粮换算', icon: 'calculator', perm: 'DRY_SYS_FORMULA', existing: true }
    ]
  },
  {
    key: 'warehouse', label: '仓库管理', icon: 'inbox',
    items: [
      // 期初库存导入移出菜单 -> 作为 农资仓 / 粮食仓 页面里的按钮入口 (URL 仍可访问)
      { key: 'wh-input',      label: '农资产品仓库', icon: 'inbox',          perm: 'DRY_WH_INPUT', placeholder: true },
      { key: 'wh-grain',      label: '粮食库',       icon: 'database',       perm: 'DRY_WH_GRAIN', placeholder: true },
      { key: 'stock-count',   label: '库存盘点',     icon: 'reconciliation', perm: 'DRY_WH_COUNT', placeholder: true },
      // [T-180] 全局库存流水查询 (按仓位/时间/类型筛选, 补"看不到退货单"等全局视角入口)
      // [2026-09-01] 下架 — 定位为付费增值功能, 商用前对所有人停用:
      //   hidden=true → 菜单全员不可见 (含 SUPPER_ADMIN 超管);
      //   perm=DRY_STOCK_LOG_GLOBAL (sys_permission 无此记录, 无人持有) → 路由守卫把 URL 直达一并拦下.
      //   恢复方式: 去掉 hidden, 并给付费客户角色绑定 DRY_STOCK_LOG_GLOBAL 权限
      { key: 'stock-log-global', label: '全局流水查询', icon: 'profile', perm: 'DRY_STOCK_LOG_GLOBAL', existing: true, hidden: true, legacy: true }
    ]
  },
  {
    key: 'finance', label: '财务模块', icon: 'pay-circle',
    items: [
      // [2026-05-22] Phase F: 真台账/付款上线, 取消 legacy 标记
      { key: 'account',        label: '客商账务',     icon: 'account-book', perm: 'DRY_ACCOUNT', existing: true },
      { key: 'payment',        label: '付款管理',     icon: 'pay-circle',   perm: 'DRY_PAYMENT', existing: true },
      // [2026-05-26] T-054C: 出库相关的收款 + 应收, 复用 DRY_ACCOUNT 权限
      { key: 'collect',        label: '收款管理',     icon: 'transaction',  perm: 'DRY_ACCOUNT', existing: true },
      // [T-111] 应收台账 + 单据核验 隐藏 — 信息已覆盖在客商账务 + 收款管理里
      // { key: 'ar-account',     label: '应收台账',     icon: 'profile',      perm: 'DRY_ACCOUNT', existing: true },
      // { key: 'receipt-verify', label: '单据核验',     icon: 'safety',       perm: 'DRY_ACCOUNT', existing: true },
      // [2026-05-28] T-061: 资金结算表 (笔级流水 + 滚动应付余额, 复用 DRY_ACCOUNT 权限) — 原"辅助结算表"
      { key: 'account-flow',   label: '资金结算表',   icon: 'reconciliation', perm: 'DRY_ACCOUNT', existing: true },
      { key: 'grain-for-seed', label: '以粮抵种',     icon: 'swap',         perm: 'DRY_GRAIN_FOR_SEED', existing: true },
      // [T-230 反向开票] 付款申请 + 开票管理 + 农户供应商管理 (随厂配置/灰度)
      { key: 'payment-apply',  label: '付款申请',     icon: 'audit',        perm: 'DRY_PAYMENT_APPLY', existing: true },
      { key: 'invoice-manage', label: '开票管理',     icon: 'file-text',    perm: 'DRY_INVOICE', existing: true },
      // [反向开票] 农户供应商管理已集成到「客商管理」页, 独立菜单隐藏 (路由保留直达)
      { key: 'farmer-supplier', label: '农户供应商',   icon: 'team',                                      existing: true, hidden: true, legacy: true }
    ]
  },
  {
    key: 'report', label: '报表管理', icon: 'bar-chart',
    items: [
      // [2026-07-23 报表重构] 8 张统计表 (完全替代旧报表中心, 对应 docs/烘干厂系统统计表格(1).xls)
      // 后端: dryingReport.aggr.AggrReportController (/drying/aggr-report/**) + 复用 finance/account/settlement
      // 单位约定: 后端返回 KG/元, 前端 ÷1000/÷10000 转换为 吨/万元
      // [2026-07-23 v2] 采购/销售合并为单页 + 维度切换(品种/客商), 行点击下钻明细, 右侧柱状图抽屉
      // [2026-09-27 改名] 四张汇总表改名统一口径 (原「采购/销售」实为粮食入库/出库聚合,
      //   数据范围不变: 粮食两张只算粮食, 农资两张只算农资), 并排到组首:
      //   采购汇总表→粮食入库汇总表 / 销售汇总表→粮食出库汇总表
      //   农资采购汇总→农资入库汇总表 / 农资销售汇总→农资出库汇总表 (key/perm 不变, 书签与角色配置不受影响)
      { key: 'report-purchase-summary',   label: '粮食入库汇总表', icon: 'bar-chart',     perm: 'DRY_RPT_PUR_VARIETY',   existing: true },
      { key: 'report-sales-summary',      label: '粮食出库汇总表', icon: 'bar-chart',     perm: 'DRY_RPT_SAL_VARIETY',   existing: true },
      { key: 'report-input-purchase',     label: '农资入库汇总表', icon: 'shopping-cart', perm: 'DRY_RPT_PUR_VARIETY',   existing: true },
      { key: 'report-input-sales',        label: '农资出库汇总表', icon: 'shop',          perm: 'DRY_RPT_SAL_VARIETY',   existing: true },
      { key: 'report-settlement',         label: '资金结算表',   icon: 'reconciliation', perm: 'DRY_ACCOUNT',           existing: true },
      { key: 'report-psi',                label: '进销存汇总表', icon: 'database',       perm: 'DRY_RPT_PSI',           existing: true },
      { key: 'report-stock',              label: '库存统计表',   icon: 'pie-chart',      perm: 'DRY_RPT_STOCK',         existing: true },
      { key: 'report-moisture',           label: '收购平均水分', icon: 'radar-chart',    perm: 'DRY_RPT_MOISTURE',      existing: true },
      { key: 'report-order-recovery',     label: '粮食订单回收统计表', icon: 'retweet',        perm: 'DRY_RPT_ORDER_RECOVERY', existing: true },
      // [2026-10-10 理论干粮] 折干对账表 — 复用 T053 占位权限 DRY_RPT_RECONCILE (原"农户对账"从未实现, T201 已绑三角色, 零新增授权)
      { key: 'report-dry-reconcile',      label: '折干对账表',   icon: 'reconciliation', perm: 'DRY_RPT_RECONCILE',     existing: true },
      // [2026-07-23 v2] 原「按品种/按客商」4 页合并为采购/销售汇总表 (维度切换), 菜单隐藏 (路由保留直达)
      { key: 'report-purchase-by-variety',  label: '采购按品种汇总', icon: 'bar-chart', perm: 'DRY_RPT_PUR_VARIETY',  existing: true, hidden: true, legacy: true },
      { key: 'report-purchase-by-customer', label: '采购按客商统计', icon: 'bar-chart', perm: 'DRY_RPT_PUR_CUSTOMER', existing: true, hidden: true, legacy: true },
      { key: 'report-sales-by-variety',     label: '销售按品种汇总', icon: 'bar-chart', perm: 'DRY_RPT_SAL_VARIETY',  existing: true, hidden: true, legacy: true },
      { key: 'report-sales-by-customer',    label: '销售按客商统计', icon: 'bar-chart', perm: 'DRY_RPT_SAL_CUSTOMER', existing: true, hidden: true, legacy: true }
    ]
  },
  {
    // [2026-07-24] 旧汇总页面 — 4 张 T-130 元数据驱动明细汇总 (明细级, 与「报表管理」聚合表互补)
    // [2026-09-27] 曾因报表管理四张汇总表改名整组隐藏, 同日恢复显示 (明细报表客户仍要用);
    //   「报表中心」(Report.vue 纯 Vuex 原型页, 数据源从未接入, 恒为空) 保持隐藏
    key: 'summary', label: '汇总管理', icon: 'bar-chart',
    items: [
      { key: 'report-grain-in',  label: '粮食入库汇总', icon: 'bar-chart', perm: 'DRY_REPORT_GRAIN_IN',  existing: true },
      { key: 'report-grain-out', label: '粮食出库汇总', icon: 'bar-chart', perm: 'DRY_REPORT_GRAIN_OUT', existing: true },
      { key: 'report-input-in',  label: '农资入库汇总', icon: 'bar-chart', perm: 'DRY_REPORT_INPUT_IN',  existing: true },
      { key: 'report-input-out', label: '农资出库汇总', icon: 'bar-chart', perm: 'DRY_REPORT_INPUT_OUT', existing: true },
      { key: 'report',           label: '报表中心',     icon: 'bar-chart', perm: 'DRY_REPORT',           existing: true, hidden: true, legacy: true }
    ]
  },
  {
    // [2026-08-31] 设备管理 — 原「系统管理→过磅设备」拆出独立成分组,
    //   并把 jinglunGo 调试页 (127.0.0.1:8765) 的驱动/读卡/监控/地磅/打印测试迁进烘干厂前端.
    //   测试页与设备配置同源 (localStorage + jinglunGo), 调试结果直接生效, 不再两地复制.
    //   全部不挂 perm (本机调试, 同设备配置定位, 全员可见).
    key: 'device', label: '设备管理', icon: 'usb',
    items: [
      // 设备配置 = 原「系统管理→过磅设备」(WeighDeviceConfig.vue). key 保持 weigh-device 不变, 老书签/页签不失效
      { key: 'weigh-device',          label: '设备配置', icon: 'dashboard',     existing: true },
      { key: 'device-drivers',        label: '驱动管理', icon: 'tool',          existing: true },
      { key: 'device-card-test',      label: '读卡测试', icon: 'idcard',        existing: true },
      { key: 'device-camera-test',    label: '监控测试', icon: 'video-camera',  existing: true },
      { key: 'device-scale-test',     label: '地磅测试', icon: 'fork',          existing: true },
      { key: 'device-triplicate-test', label: '三联测试', icon: 'printer',      existing: true },
      { key: 'device-receipt-test',   label: '小票测试', icon: 'file-text',     existing: true }
    ]
  },
  {
    key: 'sys', label: '系统管理', icon: 'setting',
    items: [
      // [2026-07-07] 菜单与导航隐藏 — 该菜单元数据只在粮易智联端编辑, 烘干厂端不开放
      // { key: 'menu-nav',       label: '菜单与导航',   icon: 'menu',         perm: 'DRY_SYS_MENU_NAV',       existing: true },
      // [2026-08-14] 消息通知/角色与权限/数据字典/基础设置 → 仅粮易智联超级管理员 (SUPPER_ADMIN) 可见,
      //   同「外设服务版本」菜单的管控方式; 烘干厂管理员 (DRY_SUPER_ADMIN) 也不可见
      { key: 'notice-mgmt',    label: '消息通知',     icon: 'notification', perm: 'DRY_SYS_NOTICE_MGMT',    existing: true, superOnly: true },
      { key: 'user',           label: '用户管理',     icon: 'user',         perm: 'DRY_SYS_USER',           existing: true },
      { key: 'role',           label: '角色与权限',   icon: 'team',         perm: 'DRY_SYS_ROLE',           existing: true, superOnly: true },
      // [2026-08-14] 干粮换算移至商品管理分组 (perm 不变: DRY_SYS_FORMULA)
      // [2026-06-25] 质价公式移至商品管理分组 (perm 不变: DRY_SYS_QUALITY_PRICE)
      // [2026-08-31] 过磅设备 → 「设备管理→设备配置」(拆出独立分组, 含外设调试测试页)
      // { key: 'weigh-device',   label: '过磅设备',   icon: 'dashboard',                                    existing: true },
      // 粮单配置 (入库/出库录磅字段开关) — 按烘干厂维度存后端, 不挂权限全员可见
      { key: 'bill-field-config', label: '粮单配置',   icon: 'form',                                         existing: true },
      { key: 'cross-platform', label: '跨端联动',     icon: 'cluster',      perm: 'DRY_SYS_CROSS_PLATFORM', existing: true },
      { key: 'op-log',         label: '操作日志',     icon: 'history',      perm: 'DRY_SYS_OP_LOG',         existing: true },
      // [T-232 反向开票] 资质管理 (厂超管提交申请 → 平台超管审核, 不挂权限全员可见)
      { key: 'qual-apply',     label: '开票资质',     icon: 'safety',                                       existing: true },
      // [反向开票] 营业员管理 / 商品管理 / 发票配置 (管理员补配, 不挂权限全员可见)
      { key: 'company-clerk',  label: '营业员管理',   icon: 'team',                                         existing: true },
      { key: 'factory-product', label: '商品管理',    icon: 'shopping',                                     existing: true },
      { key: 'invoice-config', label: '发票配置',     icon: 'setting',                                      existing: true },
      // [#5/#7/#8] 风险提示库管理 / 厂-风险绑定 / 批量配置 (平台超管维护, 不挂权限)
      { key: 'risk-tip-mgmt',  label: '风险提示库',   icon: 'warning',                                      existing: true },
      { key: 'fac-risk-assign', label: '厂风险配置',  icon: 'safety-certificate',                           existing: true },
      { key: 'fac-risk-batch', label: '风险批量配置', icon: 'thunderbolt',                                  existing: true },
      { key: 'dict',           label: '数据字典',     icon: 'profile',      perm: 'DRY_SYS_DICT',           existing: true, superOnly: true },
      { key: 'data-mapping',   label: '数据映射表',   icon: 'apartment',    perm: 'DRY_SYS_DATA_MAPPING',   placeholder: true },
      // T-020: legacy
      { key: 'settings',       label: '基础设置',     icon: 'control',      perm: 'DRY_SYS_SETTINGS',       existing: true, legacy: true, superOnly: true }
    ]
  }
]

// 扁平化所有 item, 便于 router/menuPermissions 使用
export const ALL_ITEMS = MENU_GROUPS.reduce((acc, g) => acc.concat(g.items), [])

// 非业务路由 (登录页 / 通知中心 / 个人中心 / 教程中心及其教程子页), 不受 permKeys 限制
export const PUBLIC_ROUTES = new Set([
  'notice',
  'profile',
  'tutorial',
  'tutorial-haoliangbao',
  'tutorial-alipay-invoice',
  'tutorial-formula',
  'tutorial-ai'
])
